import { Typography } from '@material-ui/core';
import { alpha, makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import PhoneInTalk from '@material-ui/icons/PhoneInTalk';
import { useTranslation } from 'react-i18next';

import FilledButton from '../FilledButton.jsx';
import UnderlinedButton from '../UnderlinedButton.jsx';

const CARD_RADIUS = 8;
// Tints derive from the brand colour (theme.palette.common.yellow) rather than a hardcoded amber, matching
// the suggestions-table banner and the alert icon below — and so white-label primaryColor overrides apply.
const ALERT_TINT_BG_OPACITY = 0.08;
const ALERT_TINT_BORDER_OPACITY = 0.35;

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
    },
    alert: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        backgroundColor: alpha(theme.palette.common.yellow, ALERT_TINT_BG_OPACITY),
        border: `1px solid ${alpha(theme.palette.common.yellow, ALERT_TINT_BORDER_OPACITY)}`,
        borderRadius: CARD_RADIUS,
        padding: 16,
        boxSizing: 'border-box',
    },
    alertIcon: {
        color: theme.palette.common.yellow,
        flexShrink: 0,
    },
    alertBody: {
        fontSize: '1rem',
        color: theme.palette.common.darkGrey,
    },
    callCard: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        backgroundColor: theme.palette.common.lightGray,
        border: `1px solid ${theme.palette.common.inputDisabledGray}`,
        borderRadius: CARD_RADIUS,
        padding: 16,
        boxSizing: 'border-box',
    },
    callIcon: {
        color: theme.palette.common.darkGrey,
        flexShrink: 0,
    },
    callHeading: {
        fontSize: '1rem',
        fontWeight: 700,
        color: theme.palette.common.darkGrey,
    },
    callBody: {
        fontSize: '0.9375rem',
        color: theme.palette.common.midGrey,
        marginTop: 4,
    },
    ctaRow: {
        display: 'flex',
    },
    pagingRow: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 24,
    },
}));

interface Props {
    variant: 'specific' | 'no_preference';
    practitionerName?: string;
    onChooseDifferent: () => void;
    hasPreviousSuggestion?: boolean;
    onPreviousSuggestion?: () => void;
}

const ChainSuggestionEmpty = ({
    variant,
    practitionerName,
    onChooseDifferent,
    hasPreviousSuggestion,
    onPreviousSuggestion,
}: Props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('xs'));

    const isSpecific = variant === 'specific';

    return (
        <div className={classes.container} data-testid="chain-suggestion-empty">
            <div className={classes.alert}>
                <ErrorOutline className={classes.alertIcon} aria-hidden="true" />
                <Typography className={classes.alertBody}>
                    {isSpecific
                        ? t('ChainNoAvailabilityBodyForPractitioner', { practitioner: practitionerName })
                        : t('ChainNoAvailabilityBody')}
                </Typography>
            </div>

            {isSpecific ? (
                <div className={classes.ctaRow}>
                    <FilledButton
                        text={t('ChainChooseDifferentDoctor')}
                        fullWidth={matchesXs}
                        onPress={onChooseDifferent}
                        dataTestId="chain-choose-different-btn"
                    />
                </div>
            ) : (
                <div className={classes.callCard} data-testid="chain-call-us-card">
                    <PhoneInTalk className={classes.callIcon} aria-hidden="true" />
                    <div>
                        <Typography className={classes.callHeading}>{t('ChainCallUsHeading')}</Typography>
                        <Typography className={classes.callBody}>{t('ChainCallUsBody')}</Typography>
                    </div>
                </div>
            )}

            {hasPreviousSuggestion && onPreviousSuggestion && (
                <div className={classes.pagingRow}>
                    <UnderlinedButton
                        noMarginTop
                        text={t('ChainPreviousSuggestion')}
                        onPress={onPreviousSuggestion}
                        dataTestId="chain-previous-suggestion-btn"
                    />
                </div>
            )}
        </div>
    );
};

export default ChainSuggestionEmpty;
